import Backbone from 'backbone'
import template from './standard-source-selector.hbs'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import dropdown from 'scoreboard/components/dropdown'
import selects from 'scoreboard/components/selects'
import reportConstants from 'scoreboard/reports/report-constants'
import formHelper from 'scoreboard/components/form-helper'
import metadata from 'scoreboard/components/metadata'
import icon from 'scoreboard/components/icon'
import appDisplay from 'scoreboard/components/app-display'
import DataSourceTypeToggle from './data-source-type-toggle'
import ObjectSelectorButton from 'scoreboard/components/object-selector-button'
import * as aggregationTypeOptions from 'scoreboard/datasets/aggregation-type-options'
import DataSourceLink from './data-source-link'
import config from 'scoreboard/components/config'

/**
 * A component for selecting a non-dataset data source (specifically scorecards
 * or initiatives). Includes toggles for Basic/Calculated. Meant to be used
 * either standalone (e.g. in reports, when a specific domain type is the only
 * choice) or as part of a multi-section toggle (e.g. in charts).
 */
const StandardSourceSelector = Backbone.View.extend({

	className: 'data-source-form',

	events: {
		'change [name=dataProperty]': 'onChangeDataProperty',
		'click .set-equation': 'onClickSetEquation',
		'change [name=aggregationType]': 'onChangeAggregationType'
	},

	/**
	 * @typedef {Object} StandardDataSource - a generic definition of a non-dataset
	 * data source (e.g. scorecard or initiative) used by reports and charts
	 *
	 * @property {DataSourceType} dataSourceType - basic or calculated
	 *
	 * @property {Integer} [domainObjectId] - for basic data sources, the ID of
	 * the domain object (e.g. the scorecardItemId)
	 *
	 * @property {String} [dataProperty] - for basic data sources, the specific
	 * field/property from the domain object to be used. This will vary by domainType
	 * and by whether the source is for a chart or report. The possible values are
	 * provided in StandardSourceSelector's dataPropertyOptions constructor option.
	 *
	 * @property {String} [equation] - for calculated data sources, the equation
	 */

	/**
	 * @param {Object} opt
	 *
	 * @param {DomainType} opt.domainType - the type of object to be selected
	 * (eg. 'scorecard-item' or 'initiative'
	 *
	 * @param {StandardDataSource} opt.dataSource - the initially selected data source
	 * settings.
	 *
	 * @param {Array|Function} opt.dataPropertyOptions - the options that should be
	 * used for the dataProperty dropdown, which are sent to dropdown.idLabels().
	 * Can be an Array or a function that returns an array.
	 *
	 * @param {Object} [opt.scorecards] - scorecard objects (not ScorecardNode models) keyed
	 * by ID that are needed for the initially selected data source settings
	 *
	 * @param {Object} [opt.initiatives] - initiative objects (not Initiative models) keyed
	 * by ID that are needed for the initially selected data source settings
	 *
	 * @param {Boolean} [opt.allowDomainObjectSelection=false] - whether there should
	 * be a selector to pick a specific domain object
	 *
	 * @param {Function} opt.createEquationEditor - function that will return a
	 * Backbone.View to serve as an equation editor. This view will be rendered
	 * in a dialog, and should return an equation in a 'done' event
	 *
	 * @param {Boolean} [opt.showAggregationTypes=false] - whether there should
	 * be a selector to pick aggregationTypes
	 *
	 * @param {String[]} [opt.excludedAggregationTypes] - aggregation types to be
	 * excluded from the select
	 *
	 * @param {Object[]} [opt.extraAggregationTypeOptions] - additional type options
	 * to display
	 *
	 * @param {Object} opt.objectSelectorOptions - options to be passed along to ObjectSelectorMenu
	 *
	 * @param {Boolean|function(): boolean} [opt.showDataProperty=() => true] -
	 * boolean value or function that returns boolean value that determines
	 * whether the dataProperty dropdown should be shown. The contents
	 * of the dropdown are determined by the dataPropertyOptions parameter (e.g.
	 * "Score", "Actual Value", etc. for scorecard item data sources)
	 */
	initialize: function (opt) {
		this.options = opt
		this.scorecards = this.options.scorecards || {}
		this.initiatives = this.options.initiatives || {}
		this.selectedDomainObjectId = this.options.dataSource.domainObjectId
		this.selectedEquation = this.options.dataSource.equation
	},

	domainType: function () {
		return this.options.domainType
	},

	section: function () {
		return metadata[this.domainType()].section
	},

	render() {
		const domainType = this.domainType()
		// #39678 Domain type label does not use standard singular type label for
		// scorecards
		const domainTypeLabel = domainType === 'scorecard-item' ?
			'scorecards.scorecardNode' : metadata[this.domainType()].singularCode
		this.$el.empty().append(template({ domainTypeLabel }))

		this._renderDataSourceType()
		this._renderDomainObjectSelector()
		this._renderDataProperty({ firstRender: true })
		this._renderSetEquationButton()

		this._updateFieldVisibility()

		this.renderAggregationType(this.options.showAggregationTypes,
			this.options.dataSource.aggregationType,this.options.dataSource.dataType,
			this.options.showAggregationTypes)

		this._renderDataSourceLink()

		return this
	},

	/** Renders the basic/calculated toggle */
	_renderDataSourceType: function () {
		this.dataSourceTypeToggle = new DataSourceTypeToggle({
			value: this.options.dataSource.dataSourceType || reportConstants.SOURCE_BASIC
		})

		this.$('.labeled-toggle-container').append(this.dataSourceTypeToggle.el)
		this.dataSourceTypeToggle.render()
		this.listenTo(this.dataSourceTypeToggle, 'change',
			this.onChangeDataSourceType.bind(this))
	},

	renderAggregationType(show, aggregationType, dataType, excludedAggregationTypes) {
		this.$('.aggregation-type').toggleClass('is-not-displayed', !show)

		let select = this.$('[name=aggregationType]').empty()
		let currentAggregationType = aggregationType
		const excludedSet = new Set(excludedAggregationTypes || [])
		const filteredDefaultOptions = aggregationTypeOptions
			.nonTotalColumn(dataType)
			.filter(it => !excludedSet.has(it.id))
		const extraOptions = this.options.extraAggregationTypeOptions
		const displayOptions = _.sortBy([
			...(extraOptions || []), ...filteredDefaultOptions
		], 'label')
		dropdown.idLabels(select, displayOptions, currentAggregationType)
	},

	_renderDomainObjectSelector: function () {
		let domainType = this.domainType()
		let section = this.section()
		let domainObject = this.selectedDomainObject()

		let titleCode
		if (domainType === 'scorecard-item') {
			titleCode = 'scorecards.scorecardNode.select'
		} else if (domainType === 'initiative') {
			titleCode = 'initiatives.initiative.select'
		}

		let selectorOptions = {
			section,
			domainObject,
			message: i18n.get(titleCode),
			objectSelectorOptions: _.extend({
				// Options for the domain object selector; these options are required
				// for charts; if they are problematic for other users of this component,
				// they can be turned into constructor options instead
				objectParams: {
					scoringType: true,
					sourceType: true
				}
			}, this.options.objectSelectorOptions)
		}

		this.objectSelectorButton = new ObjectSelectorButton(selectorOptions)

		this.objectSelectorButton.on('change', selected => this.onChangeDomainObject())

		this.$('.object-selector-button-container').append(this.objectSelectorButton.el)
		this.objectSelectorButton.render()
	},

	showDataProperty: function() {
		if (_.isFunction(this.options.showDataProperty)) {
			return this.options.showDataProperty(this.section())
		}
		return this.options.showDataProperty ?? true
	},

	/**
	 * Renders "dataProperty" dropdown, which is labeled "Field" in the UI.
	 * For scorecard items, this dropdown contains fields like "KPI Value" and
	 * "Score". For initiatives, it contains items like "% Complete" and
	 * "Budget Remaining".
	 */
	_renderDataProperty: function (opt = { firstRender: false }) {
		if (!this.showDataProperty()) {
			return
		}

		let select = this.$('[name=dataProperty]')

		let selectOptions = _.isFunction(this.options.dataPropertyOptions)
			? this.options.dataPropertyOptions({
				domainType: this.domainType(),
				domainObject: this.selectedDomainObject(),
				dataSource: this.val()
			})
			: this.options.dataPropertyOptions

		let selected = null
		if (opt.firstRender) {
			// For the initial render, just use the dataProperty that was sent in
			selected = this.options.dataSource.dataProperty
		} else {
			// If this is a re-render, then we may need to select a different value.
			// For example, we may have changed scorecard items, and the previously
			// selected threshold might not exist in the new scorecard item.
			if (this.options.defaultDataProperty) {
				let previousValue = this.val().dataProperty
				let previousSelectedOption = select.find(`option[value="${previousValue}"]`)[0]

				selected = this.options.defaultDataProperty({
					domainType: this.domainType(),
					domainObject: this.selectedDomainObject(),
					selectOptions,
					previousSelectedOption: previousSelectedOption == null ? null : {
						id: previousSelectedOption.value,
						label: previousSelectedOption.label
					}
				})
			}
		}

		if (!selected) {
			// If we weren't able to pick a good default, just use the first
			// option in the list
			selected = _.get(selectOptions[0], 'id')
		}

		if (!opt.firstRender) {
			// Empty and reset the select, since some options may
			// have the same value but have different meanings and labels
			selects(select.empty())
		}

		_.each(selectOptions, o => o.label = i18n.get(o.label))

		selects(select, {
			options: selects.applyGlobalSortRules(selectOptions),
			selected,
			accessors: {
				value: 'id'
			},
			groupBy: 'group'
		})
	},

	_renderSetEquationButton: function () {
		let equation = this.selectedEquation
		let buttonText = _.isEmpty(equation) ?
			i18n.get('scorecards.node.metric.equation.set') : equation
		this.$('.set-equation').val(equation)
		this._setEquationButtonLabel(buttonText)
	},

	_renderDataSourceLink: function() {
		this.dataSourceLink && this.dataSourceLink.remove()

		if (_.isNil(this.selectedDomainObject())) {
			return
		}

		this.dataSourceLink = new DataSourceLink({
			section: this.section(),
			id: this.selectedDomainObjectId
		})
		this.$('.source-data-link-container').append(this.dataSourceLink.el)
		this.dataSourceLink.render()
	},

	_updateFieldVisibility: function () {
		let calculated = this.val().dataSourceType === reportConstants.SOURCE_CALCULATED

		this.$('.set-equation-section').toggleClass('is-not-displayed', !calculated)

		this.$('.set-domain-object-section').toggleClass('is-not-displayed',
			!this.options.allowDomainObjectSelection || calculated)

		let showDataProperty
		if (!this.showDataProperty()) {
			showDataProperty = false
		} else if (calculated) {
			// If calculated, dataProperty doesn't apply
			showDataProperty = false
		} else {
			if (this.options.allowDomainObjectSelection) {
				// If there's a domain object selector (like in chart designer),
				// then we can only show the dataProperty input after a
				// domain object is selected
				// #46236 check that we have a legitimate domain object selected
				// instead of checking this.selectedDomainObjectId, since the
				// user may have switched between scorecards/initiatives
				const domainObject = this.selectedDomainObject()
				showDataProperty = !_.isNil(domainObject)

				// #50721 don't allow switching the field if the scoring type is unscored-text
				if (isUnscoredText(domainObject)) showDataProperty = false
			} else {
				// If no domain object selector (like in report designer), then
				// always show the dataProperty input
				showDataProperty = true
			}
		}

		this.$('.toggle-type-section').toggleClass('is-not-displayed', this.options.disallowTypeToggle)
		this.$('.data-property-section').toggleClass('is-not-displayed', !showDataProperty)
	},

	_setEquationButtonLabel: function (label) {
		this.$('.set-equation').html(_.escape(label) + icon('edit'))
	},

	selectedDomainObject: function () {
		return this[this.section()][this.selectedDomainObjectId]
	},

	onChangeDataSourceType: function () {
		this._updateFieldVisibility()
		this.trigger('change:dataSourceType')
	},

	onChangeDataProperty: function () {
		this.trigger('change:dataProperty')
	},

	onClickSetEquation: function () {
		let equationEditor = this.options.createEquationEditor({ equation: this.selectedEquation })

		equationEditor.on('done', updatedEquation => {
			this._setEquationButtonLabel(updatedEquation)
			this.selectedEquation = updatedEquation
			this.trigger('change:equation', updatedEquation)
		})
		appDisplay.dialog(equationEditor)
	},

	onChangeDomainObject: function () {
		let newDomainObject = this.objectSelectorButton.val()
		this[this.section()][newDomainObject.id] = newDomainObject
		this.selectedDomainObjectId = newDomainObject.id

		this._renderDataProperty()
		this._updateFieldVisibility()
		this._renderDataSourceLink()

		this.trigger('change:domainObject')
	},

	onChangeAggregationType: function(e) {
		const aggregationTypeDropdown = this.$('[name=aggregationType]')
		let aggregationType = aggregationTypeDropdown.val()
		this.trigger('change:aggregationType', aggregationType)
	},

	/**
	 * Returns the configured data source as a StandardDataSource
	 */
	val: function () {
		return _.extend(
			formHelper.readFromDom(this.el),
			{
				id: this.options.dataSource.id,
				dataSourceType: this.dataSourceTypeToggle.val(),
				domainObjectId: this.selectedDomainObjectId,
				domainObject: this.selectedDomainObject()
			})
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([ this.objectSelectorButton, this.dataSourceLink ], 'remove')
	}
})

const isUnscoredText = domainObject => {
	if (!_.isNil(domainObject?.scoringTypeId)) {
		const scoringType = config.scoringType(domainObject?.scoringTypeId)
		return scoringType?.kpiValueType === 'unscored-text'
	}
	return false
}

export default StandardSourceSelector
export { isUnscoredText }

StandardSourceSelector.__filename__ = 'components/data-source-selector/standard-source-selector.js'
